<template>
    <v-container fluid class="schedule px-0">
        <div class="schedule-tabs-wrapper">
            <v-btn-toggle v-model="tab" mandatory class="schedule-tabs">
                <v-btn value="upcoming" small class="schedule-tabs__btn">
                    Επόμενα
                    <span class="schedule-tabs__count">{{ upcoming.length }}</span>
                </v-btn>
                <v-btn value="results" small class="schedule-tabs__btn">
                    Αποτελέσματα
                    <span class="schedule-tabs__count">{{ results.length }}</span>
                </v-btn>
            </v-btn-toggle>
        </div>

        <template v-if="loading">
            <v-skeleton-loader v-for="n in 3" :key="n" type="card-heading, list-item-avatar-two-line" class="mb-3 rounded-lg" />
        </template>

        <div v-else-if="!activeGroups.length" class="schedule-empty">
            <v-icon x-large color="orange">mdi-basketball</v-icon>
            <div>{{ tab === 'upcoming' ? 'Δεν υπάρχουν επόμενοι αγώνες για αυτά τα φίλτρα' : 'Δεν υπάρχουν αποτελέσματα για αυτά τα φίλτρα' }}</div>
        </div>

        <template v-else>
            <div v-for="group in activeGroups" :key="group.key" class="day-group">
                <div class="day-header" :class="{ 'day-header--mobile': $vuetify.breakpoint.mobile }">
                    <span class="day-header__bar custom-orange" />
                    <span v-if="group.relative" class="day-header__relative">{{ group.relative }}</span>
                    <span class="day-header__label">{{ group.label }}</span>
                    <span class="day-header__count">{{ group.matches.length }} {{ group.matches.length === 1 ? 'αγώνας' : 'αγώνες' }}</span>
                </div>
                <div class="day-matches">
                    <match-card v-for="match in group.matches" :key="match.id" :match="match" :show-date="false" />
                </div>
            </div>
        </template>
    </v-container>
</template>

<script>
    import momentTimezone from 'moment-timezone'
    import MatchCard from '@/components/MatchCard'
    import { isMatchLive, refreshLiveStatuses, withLiveData, pollWhileVisible, LIVE_TICK_MS } from '@/utils/matchLive'

    const TZ = 'Europe/Athens'

    export default {
        components: { MatchCard },
        props: {
            matches: {
                type: Array,
                default: () => []
            },
            loading: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                tab: 'upcoming',
                now: Date.now(),
                stopTick: null
            }
        },
        computed: {
            // live score and end of game from the stat logs, ahead of the next list reload
            liveMatches() {
                return this.matches.map(match => withLiveData(match, this.now))
            },
            // Live matches sit on top of the upcoming tab, even after the backend marks them as not pending
            upcoming() {
                return this.sortByDate(this.liveMatches.filter(match => match.pending || isMatchLive(match, this.now)), 1)
            },
            results() {
                return this.sortByDate(this.liveMatches.filter(match => !match.pending && !isMatchLive(match, this.now)), -1)
            },
            activeGroups() {
                return this.groupByDay(this.tab === 'upcoming' ? this.upcoming : this.results)
            }
        },
        watch: {
            matches(matches, previous) {
                refreshLiveStatuses(this.matches)
                // a background reload of the same list keeps the tab the user is on
                const ids = list => (list || []).map(match => match.id).sort().join(',')
                if (ids(matches) !== ids(previous)) {
                    this.tab = this.upcoming.length ? 'upcoming' : 'results'
                }
            }
        },
        created() {
            refreshLiveStatuses(this.matches)
            this.stopTick = pollWhileVisible(() => {
                this.now = Date.now()
                refreshLiveStatuses(this.matches, this.now)
            }, LIVE_TICK_MS)
        },
        beforeDestroy() {
            this.stopTick()
        },
        methods: {
            sortByDate(matches, direction) {
                return [...matches].sort((a, b) => {
                    const timeA = a.matchDatetime ? momentTimezone(a.matchDatetime).valueOf() : 0
                    const timeB = b.matchDatetime ? momentTimezone(b.matchDatetime).valueOf() : 0
                    return (timeA - timeB) * direction
                })
            },
            groupByDay(matches) {
                const today = momentTimezone().tz(TZ).startOf('day')
                const groups = []
                const byKey = {}

                matches.forEach(match => {
                    const date = match.matchDatetime ? momentTimezone(match.matchDatetime).tz(TZ) : null
                    const key = date ? date.format('YYYY-MM-DD') : 'no-date'

                    if (!byKey[key]) {
                        byKey[key] = {
                            key,
                            label: date ? date.locale('el').format('dddd D MMMM YYYY') : 'Χωρίς ημερομηνία',
                            relative: date ? this.getRelativeDay(date.clone().startOf('day').diff(today, 'days')) : null,
                            matches: []
                        }
                        groups.push(byKey[key])
                    }
                    byKey[key].matches.push(match)
                })

                return groups
            },
            getRelativeDay(diff) {
                if (diff === 0) return 'Σήμερα'
                if (diff === 1) return 'Αύριο'
                if (diff === -1) return 'Χθες'
                return null
            }
        }
    }
</script>

<style scoped>
.schedule {
    max-width: 1200px;
    margin: 0 auto;
}

/* ---------- Tabs ---------- */
.schedule-tabs-wrapper {
    display: flex;
    justify-content: center;
    margin-bottom: 16px;
}

.schedule-tabs {
    background: #0d0d0f !important;
    border-radius: 8px !important;
    padding: 3px;
}

.schedule-tabs .schedule-tabs__btn {
    background: transparent !important;
    border: none !important;
    border-radius: 6px !important;
    color: rgba(255, 255, 255, 0.75) !important;
    font-family: Ubuntu, Arial, sans-serif;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: none;
    height: 36px !important;
    min-width: 140px !important;
}

.schedule-tabs .schedule-tabs__btn.v-btn--active {
    background: linear-gradient(90deg, #ef4444 0%, #ff7133 50%, orange 100%) !important;
    color: #fff !important;
}

.schedule-tabs .schedule-tabs__btn.v-btn--active::before {
    opacity: 0 !important;
}

.schedule-tabs__count {
    margin-left: 8px;
    padding: 0 7px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.2);
    font-size: 0.7rem;
}

/* ---------- Day groups ---------- */
.day-group {
    margin-bottom: 20px;
}

.day-header {
    position: sticky;
    top: 60px;
    z-index: 3;
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
    padding: 8px 14px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    font-family: Ubuntu, Arial, sans-serif;
}

.day-header--mobile {
    /* split in two: the build's CSS minifier cannot parse max() nested in calc() */
    --safe-top: max(env(safe-area-inset-top), var(--safe-area-inset-top, env(safe-area-inset-top)));

    top: calc(56px + var(--safe-top));
}

.day-header__bar {
    width: 5px;
    height: 20px;
    border-radius: 2px;
}

.day-header__relative {
    padding: 1px 8px;
    border-radius: 4px;
    background: #0d0d0f;
    color: #fff;
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.day-header__label {
    font-weight: 800;
    text-transform: capitalize;
}

.day-header__count {
    margin-left: auto;
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.8rem;
    font-weight: 600;
}

.day-matches {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

/* ---------- Empty ---------- */
.schedule-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 40px 16px;
    background: #fff;
    border-radius: 12px;
    font-family: Ubuntu, Arial, sans-serif;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.6);
    text-align: center;
}

@media (max-width: 600px) {
    .schedule-tabs-wrapper .schedule-tabs {
        display: flex;
        width: 100%;
    }

    .schedule-tabs .schedule-tabs__btn {
        flex: 1 1 0;
        min-width: 0 !important;
    }

    .day-header__count {
        display: none;
    }
}
</style>
